import {apiClient} from 'core/apiClient';
import {ProductsGridAccessors, ProductsGridRawRow} from 'types';
import {
    ApiFilters,
    ApiFilterValue,
    ApiPaging,
    ApiSortings,
    SimplePaginationContent,
    SimplePaginationContentRaw,
} from 'types/api';
import {ProductContentRawRow, ProductRaw} from 'types/products-detail';

import {PackageTypes} from '../types/gtins';
import {CodeRequestType, CodeResponseType, ValidationStatus} from '../types/validation';

import {filterMapperByKey} from './mappers/filters';

const PRODUCTS_URL = '/service/products';
const GRAPH_URL = '/service/graph';

let gridTokensCache: Record<number, string | null> = {
    0: null,
};

export function clearProductsGridCache() {
    gridTokensCache = {
        0: null,
    };
}

function modifyMarkingCodeFilters(params: {filters: ApiFilters; search: string}) {
    let result: ApiFilterValue[] | undefined = [];
    const codesFilterElementIndex = params.filters.findIndex(
        (elem) => elem.id === ProductsGridAccessors.codesFilter,
    );
    if (codesFilterElementIndex !== -1) {
        result = result.concat(params.filters[codesFilterElementIndex].value);
        params.filters.splice(codesFilterElementIndex, 1);
    }

    const codesFilterFileElementIndex = params.filters.findIndex(
        (elem) => elem.id === ProductsGridAccessors.codesFileFilter,
    );
    if (codesFilterFileElementIndex !== -1) {
        result = result.concat(params.filters[codesFilterFileElementIndex].value);
        params.filters.splice(codesFilterFileElementIndex, 1);
    }

    const packagingTypeFilter = params.filters.find(
        (f) => f.id === ProductsGridAccessors.packageType,
    );

    if (packagingTypeFilter)
        packagingTypeFilter.value = filterMapperByKey(packagingTypeFilter.value, 'value');
    else {
        const packagingTypeMapping: Record<string, PackageTypes[]> = {
            '/products/consumer': [PackageTypes.GROUP, PackageTypes.UNIT],
            '/products/transport': [PackageTypes.BOX, PackageTypes.PALLET, PackageTypes.CONTAINER],
            '/products/customs': [PackageTypes.CUSTOMS_UNIT],
        };
        params.filters.push({
            id: ProductsGridAccessors.packageType,
            value: filterMapperByKey(packagingTypeMapping[location.pathname] ?? [], 'value'),
        });
    }

    const modifiedFilter = params.filters.find((f) => f.id === ProductsGridAccessors.modifiedAt);
    if (modifiedFilter) {
        modifiedFilter.value = filterMapperByKey(modifiedFilter.value, 'range');
    }

    const groupCodeFilter = params.filters.find(
        (f) => f.id === ProductsGridAccessors.productGroupCode,
    );
    if (groupCodeFilter) groupCodeFilter.value = filterMapperByKey(groupCodeFilter.value, 'value');

    const productNameFilter = params.filters.find((f) => f.id === ProductsGridAccessors.name);
    if (productNameFilter)
        productNameFilter.value = filterMapperByKey(productNameFilter.value, 'value');

    const statusFilter = params.filters.find((f) => f.id === ProductsGridAccessors.status);
    if (statusFilter) statusFilter.value = filterMapperByKey(statusFilter.value, 'value');

    const authenticStatusFilter = params.filters.find(
        (f) => f.id === ProductsGridAccessors.authenticStatus,
    );
    if (authenticStatusFilter)
        authenticStatusFilter.value = filterMapperByKey(authenticStatusFilter.value, 'value');

    const ownerOrTinFilter = params.filters.find(
        (f) => f.id === ProductsGridAccessors.ownerTinOrName,
    );
    if (ownerOrTinFilter)
        ownerOrTinFilter.value = filterMapperByKey(ownerOrTinFilter.value, 'value');

    const storageLocationFilter = params.filters.find(
        (f) => f.id === ProductsGridAccessors.storageLocationAddress,
    );
    if (storageLocationFilter)
        storageLocationFilter.value = filterMapperByKey(storageLocationFilter.value, 'value');

    if (result.length === 0) {
        result = undefined;
    }

    // TODO ask backend to move it inside filters field
    // @ts-ignore
    params.filteringMarkingCodes = result;

    if (params.search) {
        // @ts-ignore
        params.commonFilter = params.search;
        // @ts-ignore
        delete params.search;
    }
}

export async function fetchProductsGrid(params: {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    search: string;
    pageRef?: string | null;
}): Promise<SimplePaginationContent<ProductsGridRawRow>> {
    const {pageRef, ...restParams} = params;
    modifyMarkingCodeFilters(restParams);

    const {pageSize, pageNumber} = restParams.paging;
    const mappedPaging = pageNumber === 0 ? {pageSize} : {pageRef};
    const responseData: SimplePaginationContentRaw<ProductsGridRawRow> = await apiClient
        .post(PRODUCTS_URL, {...restParams, paging: mappedPaging})
        .then((res) => res.data);

    return {
        content: responseData.data,
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
    };
}

export async function fetchProductsDetail(params: {
    gtin: string | null;
    markingCode: string;
}): Promise<ProductRaw> {
    return (await apiClient.post(`${PRODUCTS_URL}/details`, params)).data as ProductRaw;
}

export async function fetchProductContentGrid(params: {
    aggregationNumber: string;
    gtin: string | null;
    paging: ApiPaging;
    sortings: ApiSortings;
    filters: ApiFilters;
}): Promise<ProductContentRawRow[]> {
    return (await apiClient.post(`${PRODUCTS_URL}/details/children`, params))
        .data as ProductContentRawRow[];
}

export async function fetchOriginalPhoto(params: {link: string}): Promise<string> {
    const path = `/photobank/download-original-photo?code=${params.link}`;
    return apiClient
        .get(path, {responseType: 'arraybuffer'})
        .then((response) => Buffer.from(response.data, 'binary').toString('base64'));
}

export async function validateCodeWithCryptoTail(code: string) {
    const path = '/validation/codes';
    const formData = new FormData();
    formData.append('markingCode', code);
    return apiClient.post<ValidationStatus>(path, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    });
}

export async function validateCode(payload: CodeRequestType) {
    const path = '/marking/validate';
    return apiClient.post<CodeResponseType>(path, payload);
}

export async function validateCodeWithHsn(
    markingCode: string,
    declarationParams?: {
        type: 'CUSTOMS_DECLARATION';
        storageLocationId: string;
    },
) {
    const path = '/marking/validate';
    const extraParams = declarationParams || {type: 'UPD'};
    const body = {markingCode, ...extraParams};
    return apiClient.post<CodeResponseType>(path, body);
}

export async function validateCodesByArray(markingCodes: string[]) {
    const path = '/marking/validate/batch';
    const body = {markingCodes, type: 'UPD'};
    return apiClient.post<{
        data: CodeResponseType[];
        errors: {[key: number]: {code: string; message: string}[]};
    }>(path, body);
}

export async function fetchProductsNames() {
    const path = 'names';
    return (await apiClient.get<string[]>(`${PRODUCTS_URL}/${path}`)).data;
}

export async function fetchStorageLocations() {
    const path = 'storage-locations';
    return (await apiClient.get<Record<string, string>>(`${PRODUCTS_URL}/${path}`)).data;
}
